import { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { FILTER_PERIODS, FILTER_TYPES } from '../../data/portfolio'

const ITEM_BASE =
  'portfolio-filter-item flex w-full cursor-pointer items-center justify-between gap-2 rounded-control p-2 text-left text-sm/sm text-ink transition-colors outline-none bg-transparent hover:bg-ink/5 data-highlighted:bg-ink/5 data-disabled:opacity-50 data-disabled:bg-transparent'

const ITEM_SELECTED = `${ITEM_BASE} font-medium`

function CheckIcon({ visible }) {
  return (
    <span
      data-icon="true"
      className={`flex size-3 shrink-0 items-center justify-center text-ink ${
        visible ? 'opacity-85' : 'opacity-0'
      }`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 256 256"
        fill="currentColor"
        className="size-full"
      >
        <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z" />
      </svg>
    </span>
  )
}

function MenuItem({ label, selected, onSelect }) {
  return (
    <div
      role="menuitem"
      tabIndex={-1}
      data-orientation="vertical"
      className={selected ? ITEM_SELECTED : ITEM_BASE}
      onClick={() => onSelect(label)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          onSelect(label)
        }
      }}
    >
      <div className="flex items-center gap-2">
        <span>{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <CheckIcon visible={selected} />
      </div>
    </div>
  )
}

function Column({ title, options, selected, onSelect }) {
  return (
    <div className="portfolio-filter-col flex min-w-[124px] flex-col">
      <span className="portfolio-filter-heading px-2 py-1 font-mono label-meta text-muted">
        {title}
      </span>
      {options.map((option) => (
        <MenuItem
          key={option}
          label={option}
          selected={option === selected}
          onSelect={onSelect}
        />
      ))}
    </div>
  )
}

export default function FilterMenu({
  type,
  period,
  onType,
  onPeriod,
  testId,
  anchorRef,
}) {
  const [pos, setPos] = useState(null)

  useLayoutEffect(() => {
    const el = anchorRef?.current
    if (!el) return undefined
    const update = () => {
      const rect = el.getBoundingClientRect()
      setPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchorRef])

  if (!pos) return null

  return createPortal(
    <div
      data-testid={testId}
      data-side="bottom"
      data-align="end"
      data-state="open"
      data-dropdown-menu-content="true"
      role="menu"
      aria-orientation="vertical"
      tabIndex={-1}
      dir="ltr"
      className="portfolio-filter-menu z-9999 max-h-96 min-w-0 overflow-y-scroll rounded-card border-0 bg-elevated p-1 shadow-elevate-popover anim-picker-in light:bg-overlay"
      style={{
        outline: 'none',
        position: 'fixed',
        top: pos.top,
        right: pos.right,
      }}
    >
      <div className="portfolio-filter-row flex">
        <Column title="Type" options={FILTER_TYPES} selected={type} onSelect={onType} />
        <span className="portfolio-filter-rule mx-1 w-px shrink-0 self-stretch bg-line" />
        <Column
          title="Period"
          options={FILTER_PERIODS}
          selected={period}
          onSelect={onPeriod}
        />
      </div>
    </div>,
    document.body
  )
}

export const FILTER_TRIGGER_CLASS =
  'group text-sm/sm disabled:opacity-50 gap-1 rounded-pill p-1 border-transparent active:bg-ink/8 disabled:bg-fill data-[state=open]:bg-ink/8 light:border-transparent light:active:bg-surface light:disabled:bg-surface light:data-[state=open]:bg-surface shrink-0 cursor-pointer rounded-pill border-0 bg-transparent font-normal text-ink/70 shadow-none transition-colors hover:bg-ink/10 hover:text-ink aria-expanded:bg-ink/10 aria-expanded:text-ink light:bg-transparent light:bg-none light:shadow-none light:hover:bg-ink/6 light:hover:bg-none inline-flex size-6 items-center justify-center max-mobile:min-h-0 max-mobile:overflow-visible relative max-mobile:size-8 max-mobile:before:absolute max-mobile:before:-inset-[2px] max-mobile:before:content-[\'\']'

export function FilterIcon() {
  return (
    <span data-icon="true" className="flex size-4 min-w-4 shrink-0 items-center justify-center">
      <svg
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="size-full"
      >
        <path
          d="M2 5h6.1M11.9 5H14M2 11h1.6M7.4 11H14"
          stroke="currentColor"
          strokeWidth="1.25"
          strokeLinecap="round"
        />
        <circle cx="10" cy="5" r="1.9" stroke="currentColor" strokeWidth="1.25" />
        <circle cx="5.5" cy="11" r="1.9" stroke="currentColor" strokeWidth="1.25" />
      </svg>
    </span>
  )
}
